import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Typography } from '@mui/material';

import DataService, { Election } from '../../services/dataService';
import { MenuItemType } from './NavItems';

// ---------------------------------------------------------------------------
// useBreadcrumbText — resolves a pathname to a display label
// ---------------------------------------------------------------------------

export const useBreadcrumbText = (menuItems: MenuItemType[]) => {
  const { t } = useTranslation();

  const getBreadcrumbText = (path: string): string => {
    if (path.startsWith('/election/')) {
      const electionId = parseInt(path.split('/')[2]);
      const elections: Election[] = DataService.getElections();
      const election = elections.find((e) => String(e.id) === String(electionId));
      if (election) {
        const year = election.date ? new Date(election.date).getFullYear() : '';
        return `Elections / ${election.type} / ${year}`;
      }
      return 'Elections';
    }
    if (path.startsWith('/candidate/')) {
      const candidateId = parseInt(path.split('/')[2]);
      const candidate = DataService.getCandidateById(candidateId);
      if (candidate) {
        const nameParts = candidate.name.split(' ');
        const firstName = nameParts[0];
        const lastName = nameParts[nameParts.length - 1];
        return `Candidates / ${firstName} ${lastName}`;
      }
      return 'Candidates';
    }

    const findActiveItem = (items: MenuItemType[], currentPath: string): string | undefined => {
      for (const item of items) {
        if (item.path === currentPath) return item.text;
        if (item.children) {
          const found = findActiveItem(item.children, currentPath);
          if (found) return found;
        }
      }
      return undefined;
    };

    const staticLabels: Record<string, string> = {
      '/': t('breadcrumb.overview'),
      '/candidates': t('breadcrumb.candidates'),
      '/compare': t('breadcrumb.compare'),
      '/manifestos': t('breadcrumb.manifestos'),
      '/voters': t('breadcrumb.voters'),
      '/elections': t('breadcrumb.elections'),
      '/election-countdown': t('breadcrumb.elections'),
      '/results': t('breadcrumb.liveResults'),
      '/insights': t('breadcrumb.dataInsights'),
      '/data-insights': t('breadcrumb.dataInsights'),
      '/forecast': 'Forecast',
      '/networks': 'Networks',
      '/news': t('breadcrumb.newsSocial'),
      '/vote': t('breadcrumb.vote'),
    };
    if (staticLabels[path]) return staticLabels[path];
    return findActiveItem(menuItems, path) || 'Overview';
  };

  return getBreadcrumbText;
};

// ---------------------------------------------------------------------------
// HeaderBreadcrumb — renders the breadcrumb trail in the AppBar
// ---------------------------------------------------------------------------

interface HeaderBreadcrumbProps {
  menuItems: MenuItemType[];
  getBreadcrumbText: (path: string) => string;
}

export const HeaderBreadcrumb: React.FC<HeaderBreadcrumbProps> = ({
  menuItems: _menuItems,
  getBreadcrumbText,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const sharedSecondaryTypo = {
    color: 'text.secondary',
    fontWeight: 500,
    display: { xs: 'none', md: 'block' },
    fontSize: { md: '0.875rem', lg: '1rem' },
  };

  if (location.pathname.startsWith('/candidate/')) {
    const candidateId = parseInt(location.pathname.split('/')[2]);
    const candidate = DataService.getCandidateById(candidateId);
    const shortName = candidate
      ? (() => {
          const parts = candidate.name.split(' ');
          return `${parts[0]} ${parts[parts.length - 1]}`;
        })()
      : 'Unknown';
    return (
      <>
        <Typography
          component="span"
          variant="subtitle1"
          noWrap
          sx={{ ...sharedSecondaryTypo, cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
          onClick={() => navigate('/candidates')}
        >
          Candidates
        </Typography>
        <Typography component="span" variant="subtitle1" noWrap sx={{ ...sharedSecondaryTypo, mx: 0.5 }}>
          /
        </Typography>
        <Typography
          component="span"
          variant="subtitle1"
          noWrap
          sx={{ color: 'text.primary', fontWeight: 600, display: { xs: 'none', md: 'block' }, fontSize: { md: '0.875rem', lg: '1rem' } }}
        >
          {shortName}
        </Typography>
      </>
    );
  }

  if (location.pathname.startsWith('/manifesto/')) {
    const manifestoId = location.pathname.split('/')[2];
    const m = DataService.getManifestoById(manifestoId);
    return (
      <>
        <Typography
          component="span"
          variant="subtitle1"
          noWrap
          sx={{ ...sharedSecondaryTypo, cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
          onClick={() => navigate('/manifestos')}
        >
          Manifestos
        </Typography>
        <Typography component="span" variant="subtitle1" noWrap sx={{ ...sharedSecondaryTypo, mx: 0.5 }}>
          /
        </Typography>
        <Typography
          component="span"
          variant="subtitle1"
          noWrap
          sx={{
            color: 'text.primary',
            fontWeight: 600,
            display: { xs: 'none', md: 'block' },
            fontSize: { md: '0.875rem', lg: '1rem' },
            maxWidth: { md: 200, lg: 320 },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {m?.title || 'Manifesto'}
        </Typography>
      </>
    );
  }

  if (location.pathname.startsWith('/election/')) {
    const electionId = parseInt(location.pathname.split('/')[2]);
    const election = DataService.getElectionById(electionId);
    const year = election?.date ? new Date(election.date).getFullYear() : '';
    const type = election?.type || 'Election';
    return (
      <>
        <Typography
          component="span"
          variant="subtitle1"
          noWrap
          sx={{ ...sharedSecondaryTypo, cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
          onClick={() => navigate('/elections')}
        >
          Elections
        </Typography>
        <Typography component="span" variant="subtitle1" noWrap sx={{ ...sharedSecondaryTypo, mx: 0.5 }}>
          /
        </Typography>
        <Typography component="span" variant="subtitle1" noWrap sx={sharedSecondaryTypo}>
          {type}
        </Typography>
        {year && (
          <>
            <Typography component="span" variant="subtitle1" noWrap sx={{ ...sharedSecondaryTypo, mx: 0.5 }}>
              /
            </Typography>
            <Typography
              component="span"
              variant="subtitle1"
              noWrap
              sx={{ color: 'text.primary', fontWeight: 600, display: { xs: 'none', md: 'block' }, fontSize: { md: '0.875rem', lg: '1rem' } }}
            >
              {year}
            </Typography>
          </>
        )}
      </>
    );
  }

  return (
    <Typography
      variant="subtitle1"
      noWrap
      sx={{
        color: 'text.secondary',
        fontWeight: 500,
        display: { xs: 'none', md: 'block' },
        fontSize: { md: '0.875rem', lg: '1rem' },
        '&::before': { content: '"/"', mr: 0.5, color: 'divider' },
      }}
    >
      {getBreadcrumbText(location.pathname)}
    </Typography>
  );
};
